import { useRef, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import {
  FLOOR_PAGE_SCAN_TARGET_PROPS,
  useFloorPageScanCapture,
} from "@/lib/floorPageScanCapture";
import { usePageBreadcrumb } from "@/lib/navigationTrailContext";
import {
  inventoryAdjustScanTarget,
  type InventoryAdjustScanMode,
} from "@/pages/shipping/inventoryAdjustScanModel";

const COPY: Record<
  InventoryAdjustScanMode,
  { title: string; breadcrumb: string; hoverHint: string; placeholder: string; scanError: string }
> = {
  carton: {
    title: "Carton adjust",
    breadcrumb: "Carton adjust",
    hoverHint:
      "Scan a unique carton Data Matrix to recount quantity, split, combine, or reprint the 4x2 label.",
    placeholder: "Scan carton Data Matrix or paste hub /i/ URL",
    scanError: "Could not read that carton scan. Use the carton Data Matrix or a hub /i/ URL.",
  },
  location: {
    title: "Location adjust",
    breadcrumb: "Location adjust",
    hoverHint: "Scan a SKU at location tag to change Megaventory on-hand for that bin or shelf.",
    placeholder: "Scan location tag or paste hub /inv/ URL",
    scanError: "Could not read that location tag. Use a shelf or bin Data Matrix with SKU and location.",
  },
};

export function InventoryCartonAdjustScanPage(): JSX.Element {
  return <InventoryAdjustScanPage mode="carton" />;
}

export function InventoryLocationAdjustScanPage(): JSX.Element {
  return <InventoryAdjustScanPage mode="location" />;
}

function InventoryAdjustScanPage({ mode }: { mode: InventoryAdjustScanMode }): JSX.Element {
  const copy = COPY[mode];
  usePageBreadcrumb(copy.breadcrumb);
  const navigate = useNavigate();
  const scanRef = useRef<HTMLInputElement>(null);
  const [scanError, setScanError] = useState<string | null>(null);

  const handleScan = (raw: string) => {
    const target = inventoryAdjustScanTarget(mode, raw);
    if (target == null) {
      setScanError(copy.scanError);
      return;
    }
    setScanError(null);
    navigate(target);
  };

  useFloorPageScanCapture({
    inputRef: scanRef,
    onScan: handleScan,
  });

  return (
    <div className="space-y-4">
      <PageTitleWithHelp hoverHint={copy.hoverHint}>{copy.title}</PageTitleWithHelp>

      <Card>
        <CardHeader>
          <CardTitle>Scan to open</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <div>
            <Label htmlFor={`inventory-${mode}-adjust-scan`}>Scan or paste</Label>
            <Input
              id={`inventory-${mode}-adjust-scan`}
              ref={scanRef}
              className="text-base"
              autoComplete="off"
              placeholder={copy.placeholder}
              {...FLOOR_PAGE_SCAN_TARGET_PROPS}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  const value = event.currentTarget.value;
                  event.currentTarget.value = "";
                  handleScan(value);
                }
              }}
            />
            {scanError != null ? (
              <p role="alert" className="mt-2 flex items-center gap-2 text-sm text-red-600">
                <AlertTriangle className="h-4 w-4" />
                {scanError}
              </p>
            ) : null}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
